'use client';

import React, {useState} from 'react';
import {Link} from '@/i18n/navigation';
import {Typography} from '@mui/material';
import {trackCtaClick} from '@/lib/analytics';
import {
  StyledCtaItemRoot,
  StyledCtaItemImageBox,
  StyledCtaItemContent,
  StyledCtaItemArrow,
} from '@/components/sections/cta-cards-section/CtaItem.styles';
import RightArrowIcon from '@/components/ui/icons/RightArrow';
import ContentfulImage from '@/components/ui/image/ContentfulImage';
import {ModalSectionItem} from '@/lib/ensure-link-button';
import PopupSectionModal from '@/components/ui/modal/PopupSectionModal';

type CtaItemProps = {
  heading: string;
  imageUrl: string;
  href: string;
  isExternal?: boolean;
  modalSections?: ReadonlyArray<ModalSectionItem | null> | null;
  breadText?: string | null | undefined;
  focalX?: number | null;
  focalY?: number | null;
  contentId: string;
};

const CtaItem = React.memo(function CtaItem({
  heading,
  imageUrl,
  href,
  isExternal,
  modalSections,
  breadText,
  focalX,
  focalY,
  contentId,
}: CtaItemProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const hasModalSections = Boolean(modalSections && modalSections.some(Boolean));
  const hasBread = Boolean(breadText && breadText.trim() !== '');

  const inner = (
    <StyledCtaItemRoot className={hasBread ? 'has-bread' : ''}>
      <StyledCtaItemImageBox>
        <ContentfulImage
          src={imageUrl}
          alt={heading}
          width={96}
          height={96}            // arbitrary — only used for aspect ratio/intrinsic sizing
          sizes='88px'
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '5px',
          }}
          focalX={focalX}
          focalY={focalY}
        />
      </StyledCtaItemImageBox>

      <StyledCtaItemContent>
        <Typography className='heading' variant='h4' component='p'>
          {heading}
        </Typography>
        {hasBread && (
          <Typography className='bread' variant='body2'>
            {breadText}
          </Typography>
        )}
      </StyledCtaItemContent>

      <StyledCtaItemArrow>
        <RightArrowIcon size={20} color='currentColor' />
      </StyledCtaItemArrow>
    </StyledCtaItemRoot>
  );

  // Modal sections take precedence over any link configuration on the card.
  if (hasModalSections) {
    const handleModalClick = () => {
      trackCtaClick({buttonText: heading, targetUrl: href, buttonType: 'modal', contentId});
      setModalOpen(true);
    };

    return (
      <>
        <button
          type='button'
          onClick={handleModalClick}
          aria-label={heading}
          style={{
            display: 'block',
            width: '100%',
            padding: 0,
            border: 'none',
            background: 'none',
            textAlign: 'inherit',
            textDecoration: 'none',
            cursor: 'pointer',
          }}
        >
          {inner}
        </button>
        <PopupSectionModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          sections={modalSections!}
          ariaLabel={heading}
        />
      </>
    );
  }

  const handleClick = () => {
    trackCtaClick({buttonText: heading, targetUrl: href, buttonType: 'cta_card', contentId});
  };

  if (isExternal) {
    return (
      <a
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        style={{textDecoration: 'none'}}
        onClick={handleClick}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link href={href} style={{textDecoration: 'none'}} onClick={handleClick}>
      {inner}
    </Link>
  );
});

export default CtaItem;
